import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { api } from '@/lib/api';
import { Gauge, Infinity as InfinityIcon, Timer } from 'lucide-react';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const useCountdown = (iso) => {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        if (!iso) return undefined;
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, [iso]);
    if (!iso) return null;
    const ms = Math.max(0, new Date(iso).getTime() - now);
    const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000), s = Math.floor((ms % 60000) / 1000);
    return { done: ms === 0, text: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` };
};

export const LimitUsageBar = () => {
    const [d, setD] = useState(null);
    const load = () => api.get('/recharge/limits').then(r => setD(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);
    const cd = useCountdown(d?.used_today > 0 ? d?.resets_at : null);
    useEffect(() => { if (cd?.done) load(); }, [cd?.done]);
    if (!d) return null;

    if (d.exempt) {
        return (
            <Card className="p-4 flex items-center gap-3" data-testid="limit-usage-bar">
                <div className="h-9 w-9 rounded-full bg-emerald-50 flex items-center justify-center"><InfinityIcon className="h-5 w-5 text-emerald-600" /></div>
                <div><div className="text-sm font-semibold">No recharge limit</div><div className="text-xs text-muted-foreground">Your account is whitelisted — recharge any amount.</div></div>
            </Card>
        );
    }
    const pct = Math.min(100, (d.used_today / d.daily_limit) * 100);
    const color = pct >= 100 ? '#DC2626' : pct >= 70 ? '#F59E0B' : '#1F9D8A';
    return (
        <Card className="p-4" data-testid="limit-usage-bar">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-sm font-semibold"><Gauge className="h-4 w-4" style={{ color }} /> 24-hour recharge limit</div>
                <div className="text-sm tabular-nums" data-testid="limit-usage-text"><strong>{inr(d.used_today)}</strong> <span className="text-muted-foreground">of {inr(d.daily_limit)} used</span></div>
            </div>
            <div className="mt-3 h-2.5 rounded-full bg-secondary overflow-hidden" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
                <div className="h-full rounded-full transition-[width] duration-700 ease-out" style={{ width: `${pct}%`, backgroundColor: color }} data-testid="limit-usage-fill" />
            </div>
            <div className="mt-2 flex items-center justify-between gap-3 flex-wrap text-xs text-muted-foreground">
                <span data-testid="limit-usage-remaining">You can recharge <strong className="text-foreground">{inr(d.remaining)}</strong> more right now</span>
                {cd && !cd.done && (
                    <span className="inline-flex items-center gap-1" data-testid="limit-usage-countdown">
                        <Timer className="h-3.5 w-3.5" /> Full limit restores in <strong className="text-foreground tabular-nums">{cd.text}</strong>
                    </span>
                )}
            </div>
        </Card>
    );
};
